import type React from 'react';
import type { Editor } from '@tiptap/react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { COLORS } from 'muiTheme/colors';
import { TOOLBAR_DIMENSION, TOOLBAR_FONT } from 'components/editor/GridDndEditor/TiptapEditor/helpers/toolbarStyleSettings';
import { PyMuiIconButton } from 'components/editor/GridDndEditor/TiptapEditor/components/generalComponents';
import PyDivider from 'components/editor/GridDndEditor/TiptapEditor/components/generalComponents/PyDivider';
import FontFamilyButton from './components/FontFamilyButton';
import FontSizeButton from './components/FontSizeButton';
import EmailTemplatePickerButton, { type EmailTemplateToolbarOption } from './components/EmailTemplatePickerButton';

import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatClearIcon from '@mui/icons-material/FormatClear';

import AlignmentMenu from './components/AlignmentMenu';
import TextColorButton from './components/TextColorButton';
import LinkButton from './components/LinkButton';

type ToolbarProps = {
  editor: Editor | null;
  emailTemplateOptions?: EmailTemplateToolbarOption[];
  isLoadingEmailTemplates?: boolean;
  showEmailTemplateOptions?: boolean;
  onEmailTemplateSelect?: (option: EmailTemplateToolbarOption) => void;
};

const Toolbar: React.FC<ToolbarProps> = ({
  editor,
  emailTemplateOptions = [],
  isLoadingEmailTemplates,
  showEmailTemplateOptions,
  onEmailTemplateSelect,
}) => {
  if (!editor) return null;

  const canToggleBold = editor.can().chain().focus().toggleBold().run();
  const canToggleItalic = editor.can().chain().focus().toggleItalic().run();
  const canToggleUnderline = editor.can().chain().focus().toggleUnderline().run();

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        backgroundColor: COLORS.white,
        padding: '4px 0 4px 5px',
        width: '100%',
        maxWidth: TOOLBAR_DIMENSION.width,
        height: '40px',
        ...TOOLBAR_FONT,
      }}
      data-testid="email-template-toolbar"
    >
      {showEmailTemplateOptions && onEmailTemplateSelect && (
        <>
          <EmailTemplatePickerButton
            editor={editor}
            disabled={isLoadingEmailTemplates}
            options={emailTemplateOptions}
            onSelect={onEmailTemplateSelect}
          />
          <PyDivider />
        </>
      )}
      <FontFamilyButton editor={editor} />
      <PyDivider />
      <FontSizeButton editor={editor} />

      <Box sx={{ display: 'flex', flexDirection: 'row', gap: '1px' }}>
        <PyMuiIconButton
          attributeName="bold"
          disabled={!canToggleBold}
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
        >
          <FormatBoldIcon />
        </PyMuiIconButton>

        <PyMuiIconButton
          attributeName="italic"
          disabled={!canToggleItalic}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
        >
          <FormatItalicIcon />
        </PyMuiIconButton>

        <PyMuiIconButton
          attributeName="underline"
          disabled={!canToggleUnderline}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
        >
          <FormatUnderlinedIcon />
        </PyMuiIconButton>

        <TextColorButton editor={editor} />
      </Box>

      <PyDivider />

      <AlignmentMenu editor={editor} />

      <PyMuiIconButton
        attributeName="bulletList"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive('bulletList')}
      >
        <FormatListBulletedIcon />
      </PyMuiIconButton>

      <PyMuiIconButton
        attributeName="orderedList"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive('orderedList')}
      >
        <FormatListNumberedIcon />
      </PyMuiIconButton>

      <PyDivider />

      <LinkButton editor={editor} />

      <PyMuiIconButton attributeName="formatClear" onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}>
        <FormatClearIcon />
      </PyMuiIconButton>
    </Stack>
  );
};

export default Toolbar;
